import { useCallback } from 'react';

import { useSearchParams } from './useSearchParams';
import { getStorageKey } from '/src/utils/location';
import { decodeQueryParamValue, encodeQueryParamValue } from '/src/utils/query-param-codec';
import { getQueryParamKeys } from '/src/utils/queryParamAliases';

export function useQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    (key, fallback) => {
      const { preferredKey, fallbackKey } = getQueryParamKeys(key);
      const value = decodeQueryParamValue(
        searchParams.get(preferredKey) || (fallbackKey ? searchParams.get(fallbackKey) : null),
      );
      const storedValue =
        window.localStorage.getItem(getStorageKey(preferredKey)) ||
        (fallbackKey ? window.localStorage.getItem(getStorageKey(fallbackKey)) : null);

      return value || storedValue || fallback;
    },
    [searchParams],
  );

  // TODO: we can optimize this to not update the url if the value is already set
  // but doing so breaks a bunch of tests.
  const set = useCallback(
    (key, value, persist = false, keyPersistPrefix, replace = true) => {
      const { preferredKey, fallbackKey } = getQueryParamKeys(key);

      if (persist && value) {
        window.localStorage.setItem(getStorageKey(preferredKey, keyPersistPrefix), value);
        if (fallbackKey) {
          window.localStorage.removeItem(getStorageKey(fallbackKey, keyPersistPrefix));
        }
      } else if (persist && !value) {
        window.localStorage.removeItem(getStorageKey(preferredKey, keyPersistPrefix));
        if (fallbackKey) {
          window.localStorage.removeItem(getStorageKey(fallbackKey, keyPersistPrefix));
        }
      }

      setSearchParams(
        (x) => {
          if (!value) {
            x.delete(preferredKey);
            if (fallbackKey) {
              x.delete(fallbackKey);
            }
          } else {
            x.set(preferredKey, encodeQueryParamValue(value));
            if (fallbackKey) {
              x.delete(fallbackKey);
            }
          }
          return x;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const setAll = useCallback(
    (state, opts) => {
      /*
        Usage:
        setAll({ elapsedTime: 0, persist: true });
        Results in a URL with:
        ?elapsedTime=0&persist=true
      */
      setSearchParams(
        (x) => {
          for (const [key, value] of Object.entries(state)) {
            const { preferredKey, fallbackKey } = getQueryParamKeys(key);
            const shouldPersist = (opts?.persistKeys ?? []).includes(key) || (opts?.persist ?? false);
            if (value === undefined || value === null) {
              x.delete(preferredKey);
              if (fallbackKey) {
                x.delete(fallbackKey);
              }
              if (shouldPersist) {
                localStorage.removeItem(getStorageKey(preferredKey, opts?.keyPersistPrefix));
                if (fallbackKey) {
                  localStorage.removeItem(getStorageKey(fallbackKey, opts?.keyPersistPrefix));
                }
              }
            } else {
              x.set(preferredKey, encodeQueryParamValue(value));
              if (fallbackKey) {
                x.delete(fallbackKey);
              }
              if (shouldPersist) {
                localStorage.setItem(getStorageKey(preferredKey, opts?.keyPersistPrefix), value);
                if (fallbackKey) {
                  localStorage.removeItem(getStorageKey(fallbackKey, opts?.keyPersistPrefix));
                }
              }
            }
          }
          return x;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const remove = useCallback(
    (key, persist = false, keyPersistPrefix) => {
      const { preferredKey, fallbackKey } = getQueryParamKeys(key);

      setSearchParams(
        (x) => {
          x.delete(preferredKey);
          if (fallbackKey) {
            x.delete(fallbackKey);
          }
          if (persist) {
            window.localStorage.removeItem(getStorageKey(preferredKey, keyPersistPrefix));
            if (fallbackKey) {
              window.localStorage.removeItem(getStorageKey(fallbackKey, keyPersistPrefix));
            }
          }
          return x;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const removeAll = useCallback(
    (keys, persist, keyPersistPrefix) => {
      setSearchParams(
        (x) => {
          for (const key of keys) {
            const { preferredKey, fallbackKey } = getQueryParamKeys(key);
            x.delete(preferredKey);
            if (fallbackKey) {
              x.delete(fallbackKey);
            }
            if (persist) {
              window.localStorage.removeItem(getStorageKey(preferredKey, keyPersistPrefix));
              if (fallbackKey) {
                window.localStorage.removeItem(getStorageKey(fallbackKey, keyPersistPrefix));
              }
            }
          }
          return x;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  function getAll() {
    return Object.fromEntries([...searchParams.entries()].map(([key, value]) => [key, decodeQueryParamValue(value)]));
  }

  return {
    get,
    getAll,
    set,
    setAll,
    remove,
    removeAll,
  };
}
